"use client";

import Link from "next/link";
import Image from "next/image";
import logoWhite from "@/public/icons/logo_white.svg";
import logoDark from "@/public/icons/logo.svg";
import { MobileMenu } from "./mobile-menu";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";

const HEADER_HEIGHT = 72;
const PROBE_Y = HEADER_HEIGHT / 2;

export function Header() {
  const pathname = usePathname();
  const [isOverDark, setIsOverDark] = useState(false);

  useEffect(() => {
    let raf = 0;

    const measure = () => {
      raf = 0;
      const zones = document.querySelectorAll<HTMLElement>(
        "[data-header-theme='dark']",
      );
      let dark = false;
      zones.forEach((z) => {
        const rect = z.getBoundingClientRect();
        if (rect.top < PROBE_Y && rect.bottom > PROBE_Y) {
          dark = true;
        }
      });
      setIsOverDark(dark);
    };

    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(measure);
    };

    // Synchronous measure on mount / route change.
    measure();
    // Catch DOM that mounts later in the same tick (motion components, async data).
    const rafId = requestAnimationFrame(measure);
    const t1 = setTimeout(measure, 100);
    const t2 = setTimeout(measure, 350);

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
      cancelAnimationFrame(rafId);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [pathname]);

  return (
    <nav
      className={cn(
        "h-[72px] z-50 fixed top-0 left-0 right-0 w-full flex items-center justify-between px-4 md:px-12 transition-colors duration-300",
        isOverDark ? "text-background" : "text-foreground"
      )}
    >
      <div className="flex items-center justify-between w-full">
        <Link
          href="/"
          className="text-[27px] tracking-[-0.04em] leading-[110%] font-medium hover:opacity-80"
        >
          <Image
            aria-label="logo"
            src={isOverDark ? logoWhite : logoDark}
            alt="logo"
            width={150}
            height={150}
            className="w-[150px] h-auto"
            priority
            loading="eager"
          />
        </Link>

        <MobileMenu />
      </div>
    </nav>
  );
}
